import React, { useRef } from "react";
import { PinData, PinType } from "../types";
import { PIN_CONFIG, PIN_COLORS, TAP_CONFIG } from "../constants";

interface PinLayerProps {
  pins: PinData[];
  rotation?: number;
  onRemovePin?: (id: string) => Promise<void>;
}

/**
 * ピン表示レイヤーコンポーネント
 * ピンの表示と削除インタラクションを管理
 */
const PinLayer: React.FC<PinLayerProps> = ({
  pins,
  rotation = 0,
  onRemovePin,
}) => {
  const timersRef = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});

  const startLongPress = (id: string) => (e: React.PointerEvent) => {
    if (!onRemovePin) return;
    e.preventDefault();
    e.stopPropagation();
    // ネイティブイベントの伝播も停止
    (e as any).nativeEvent?.stopImmediatePropagation?.();

    const prev = timersRef.current[id];
    if (prev) {
      clearTimeout(prev);
    }

    console.log(`🔴 Long press started for pin ${id}`);
    timersRef.current[id] = setTimeout(() => {
      console.log(`⚠️ Removing pin ${id}`);
      onRemovePin(id).catch((error) => {
        console.error(`Failed to remove pin ${id}:`, error);
      });
      timersRef.current[id] = null;
    }, TAP_CONFIG.LONG_PRESS_MS);
  };

  const cancelLongPress = (id: string) => (e?: React.PointerEvent) => {
    if (e) {
      e.stopPropagation();
      (e as any).nativeEvent?.stopImmediatePropagation?.();
    }
    const t = timersRef.current[id];
    if (t) {
      console.log(`❌ Long press cancelled for pin ${id}`);
      clearTimeout(t);
      timersRef.current[id] = null;
    }
  };

  return (
    <>
      {pins.map((pin) => {
        const elapsedMs = Date.now() - pin.createdAt;
        const elapsedSeconds = Math.floor(elapsedMs / 1000);

        // 透明度を計算（フェードアウト）
        let opacity = 1 - elapsedMs / (PIN_CONFIG.MAX_DURATION_SECONDS * 1000);
        if (opacity < 0) opacity = 0;

        const size = PIN_CONFIG.DEFAULT_SIZE_PX;
        const fontSize = Math.max(
          PIN_CONFIG.MIN_FONT_SIZE_PX,
          Math.floor(size * PIN_CONFIG.FONT_SIZE_RATIO)
        );

        return (
          <div
            key={pin.id}
            onPointerDown={startLongPress(pin.id)}
            onPointerMove={(e) => {
              // ピン上でのポインター移動は親に伝播させない
              e.stopPropagation();
              (e as any).nativeEvent?.stopImmediatePropagation?.();
            }}
            onPointerUp={cancelLongPress(pin.id)}
            onPointerLeave={cancelLongPress(pin.id)}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            style={{
              position: "absolute",
              left: `${pin.xRatio * 100}%`,
              top: `${pin.yRatio * 100}%`,
              transform: "translate(-50%, -50%)",
              width: `${size}px`,
              height: `${size}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: PIN_COLORS[pin.tag],
              fontWeight: "bold",
              fontSize: `${fontSize}px`,
              opacity: opacity,
              userSelect: "none",
              cursor: onRemovePin ? "pointer" : "default",
              pointerEvents: "auto",
            }}
          >
            <span
              style={{
                display: "inline-block",
                transform: `rotate(${-rotation}deg)`,
                transformOrigin: "center center",
                lineHeight: 1,
              }}
            >
              {elapsedSeconds}
            </span>
          </div>
        );
      })}
    </>
  );
};

export default PinLayer;
